import { IAPIRoutes } from "../../../../config";

import { ITimeProvider } from "../providers/provider";
import {
  formatDaily3DData,
  formatDailyData,
  formatHourly3DData,
  formatMotion,
  processCDLData,
} from "../workers/cdlWorker";
import CDLWS from "./cdl_ws.js";

const env = import.meta.env;

const instance = await CDLWS();

/* Input: base64-encoded string containing CDL timestamps
 * Output: array of Date elements
 */
export const decode_cdl_timestamps = async (b64Ts: string) => {
  const fetchUrl = "data:application/octet-binary;base64," + b64Ts;
  const rawFetch = await fetch(fetchUrl);
  const rawTs = await rawFetch.arrayBuffer();
  const rawTsU8 = new Uint8Array(rawTs);

  const ptrIn = instance._malloc(rawTsU8.length);
  instance.HEAP8.set(rawTsU8, ptrIn);
  const ptrOut = instance._cdl_decode_ts(ptrIn, rawTsU8.length);
  if (!ptrOut) {
    throw new Error("TS conversion failed.");
  }

  const nTsOut = instance.HEAPF64[ptrOut / Float64Array.BYTES_PER_ELEMENT];

  const outArr = [];
  for (let i = 0; i < nTsOut; i++) {
    outArr.push(
      new Date(
        instance.HEAPF64[ptrOut / Float64Array.BYTES_PER_ELEMENT + i + 1]
      )
    );
  }

  instance._free(ptrIn);
  instance._free(ptrOut);

  return outArr;
};

const rrCDLFields = "rr_3s.ds_hrly,rr_3s.ds_dly";
const mvmtFields = `mvmt_in_range.ds_min.thr(>10000).ds_hour_sum.agg_hourly_mean`;
const bedExitFields = `bed_exit_prob.ma(5).thr(>0.65).cooldown(40).ds_hour_sum.agg_hourly_mean`;
const heartRateFields = `hr_3s.ds_hrly,hr_3s.ds_dly`;

export const CDLRepositoryFactory = ({
  APIProvider,
  APIProvider2,
  AuthService,
  APIRoutes,
  timeProvider,
}: {
  APIProvider: IAPIProvider;
  APIProvider2: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
  timeProvider: ITimeProvider;
}) => {
  const getPatientDataFromCDL: ICDLRepository["getPatientDataFromCDL"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider2;
    const { getAuthenticationAccessToken } = AuthService;

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: rrCDLFields,
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL };

    if (apiResponse) {
      // Check response type

      const { timezone } = apiResponse[patientId];
      const processedData = await processCDLData(apiResponse[patientId]);
      const processedHourlyData = formatHourly3DData(
        processedData,
        "rr_3s.ds_hrly",
        "rr_3s",
        timezone
      );

      const processedDailyData = formatDaily3DData(
        processedData,
        "rr_3s.ds_dly",
        "rr_3s",
        timezone
      );
      const categorizedData: AwaitableReturn<getPatientDataFromCDL>["data"] = {
        "1D": [],
        "3D": [],
        "5D": [],
        "30D": [],
        dailyExtra: [],
        hourlyExtra: [],
      };

      const thirtyDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(31, "days");
      const thirtySevenDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(37, "days");
      processedDailyData.forEach((item) => {
        // Convert the item's timestamp into a date/time object using the appropriate library method
        const date = timeProvider.dateLibrary(item.dt);

        // Check if the item's date is after the threshold
        if (date.isAfter(thirtyDaysAgo)) {
          categorizedData["30D"].push(item);
        }
        if (date.isAfter(thirtySevenDaysAgo)) {
          categorizedData["dailyExtra"].push(item);
        }
      });
      processedHourlyData.forEach((item) => {
        const date = timeProvider.dateLibrary.tz(item.dt, timezone);
        const now = timeProvider.dateLibrary(params.date_to);

        // Last 24 hours
        if (date.isAfter(now.clone().subtract(25, "hours"))) {
          categorizedData["1D"].push(item);
        }
        // Last 72 hours
        if (date.isAfter(now.clone().subtract(73, "hours"))) {
          categorizedData["3D"].push(item);
        }
        // Last 120 hours (5 days)
        if (date.isAfter(now.clone().subtract(121, "hours"))) {
          categorizedData["5D"].push(item);
        }
        if (date.isAfter(now.clone().subtract(145, "hours"))) {
          categorizedData["hourlyExtra"].push(item);
        }
      });
      return {
        success: true,
        data: categorizedData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  const getPatientDailyRRData: ICDLRepository["getPatientDailyRRData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider;
    const { getAuthenticationAccessToken } = AuthService;

    const response = (await GET(
      APIRoutes.apiPatientGraphData.replace(
        "{patient_id}",
        patientId?.toString()
      ),
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: "rr_3s.ds_dly",
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as CDL_API_RESPONSE_INTERNAL;
    if (response.status !== 404) {
      // Check response type

      const processedData = await processCDLData(response);
      const processedDailyData = formatDailyData(
        processedData,
        "rr_3s.ds_dly",
        "rr_3s",
        response.timezone
      );
      return {
        success: true,
        data: processedDailyData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  const getPatientDailyRRWithoutProcessing: ICDLRepository["getPatientDailyRRWithoutProcessing"] =
    async (payload) => {
      const { patient_id: patientId } = payload;
      const { ...params } = payload;
      const { GET } = APIProvider;
      const { getAuthenticationAccessToken } = AuthService;
      const response = (await GET(
        APIRoutes.apiPatientGraphData.replace(
          "{patient_id}",
          patientId?.toString()
        ),
        {
          date_from: params.date_from,
          date_to: params.date_to,
          db: "3s",
          fields: "rr_3s.ds_dly",
        },
        {
          Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
        },
        payload.signal
      )) as CDL_API_RESPONSE_INTERNAL;
      if (response.status !== 404) {
        // Check response type

        return {
          success: true,
          data: response,
        };
      }

      return {
        success: false,
        error: { message: "Something went wrong" },
      };
    };

  const getPatientHourlyRRWithoutProcessingV2: ICDLRepository["getPatientHourlyRRWithoutProcessingV2"] =
    async (payload) => {
      const { patient_ids: patientIds, signal, date_from, date_to } = payload;
      const { getAuthenticationAccessToken } = AuthService;

      try {
        const token = await getAuthenticationAccessToken();

        // Construct the query parameters
        const queryParams = new URLSearchParams({
          date_from,
          date_to,
          db: "3s",
          fields: "rr_3s.ds_hrly",
        });

        patientIds.forEach((id) => {
          queryParams.append("patient_ids", id.toString());
        });

        const response = await fetch(
          `${env.VITE_API_GATEWAY_URL_CDL_DEV}${
            APIRoutes.apiPatientGraphDataV2
          }?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal, // Attach the abort signal
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data: { [patientId: string]: CDL_API_RESPONSE_INTERNAL } =
          await response.json();

        return {
          success: true,
          data,
        };
      } catch (error) {
        // @ts-ignore
        if (error && error?.name === "AbortError") {
          return {
            success: false,
            error: { message: "Request was aborted" },
          };
        }

        console.error("Fetch error:", error);
        return {
          success: false,
          error: { message: "Something went wrong" },
        };
      }
    };

  const getPatientHourlyHRWithoutProcessingV2: ICDLRepository["getPatientHourlyHRWithoutProcessingV2"] =
    async (payload) => {
      const { patient_ids: patientIds, signal, date_from, date_to } = payload;
      const { getAuthenticationAccessToken } = AuthService;

      try {
        const token = await getAuthenticationAccessToken();

        // Construct the query parameters
        const queryParams = new URLSearchParams({
          date_from,
          date_to,
          db: "HR3s",
          fields: "hr_3s.ds_hrly",
        });

        patientIds.forEach((id) => {
          queryParams.append("patient_ids", id.toString());
        });

        const response = await fetch(
          `${env.VITE_API_GATEWAY_URL_CDL_DEV}${
            APIRoutes.apiPatientGraphDataV2
          }?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal, // Attach the abort signal
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data: { [patientId: string]: CDL_API_RESPONSE_INTERNAL } =
          await response.json();

        return {
          success: true,
          data,
        };
      } catch (error) {
        // @ts-ignore
        if (error && error?.name === "AbortError") {
          return {
            success: false,
            error: { message: "Request was aborted" },
          };
        }

        console.error("Fetch error:", error);
        return {
          success: false,
          error: { message: "Something went wrong" },
        };
      }
    };

  const getBedExitMotionData: ICDLRepository["getBedExitMotionData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider;
    const { getAuthenticationAccessToken } = AuthService;

    const response = (await GET(
      APIRoutes.apiPatientGraphData.replace(
        "{patient_id}",
        patientId?.toString()
      ),
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: `${mvmtFields},${bedExitFields}`,
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as CDL_API_RESPONSE_INTERNAL;

    if (response.message !== "No Circadia data found for the patient") {
      const processedData = await processCDLData(response);
      const processedMotionData = formatMotion(
        processedData,
        "mvmt_in_range.ds_min.thr(>10000).ds_hour_sum.agg_hourly_mean",
        "mvmt_in_range"
      );

      const processedBedExitData = formatMotion(
        processedData,
        "bed_exit_prob.ma(5).thr(>0.65).cooldown(40).ds_hour_sum.agg_hourly_mean",
        "bed_exit_prob"
      );

      return {
        success: true,
        data: {
          bedExit: processedBedExitData,
          motion: processedMotionData,
        },
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  const getHeartRateData: ICDLRepository["getHeartRateData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider2;
    const { getAuthenticationAccessToken } = AuthService;

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "HR3s",
        fields: heartRateFields,
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL };

    if (apiResponse) {
      // Check response type

      const { timezone } = apiResponse[patientId];
      const processedData = await processCDLData(apiResponse[patientId]);

      const processedHourlyData = formatHourly3DData(
        processedData,
        "hr_3s.ds_hrly",
        "hr_3s",
        timezone
      );

      const processedDailyData = formatDaily3DData(
        processedData,
        "hr_3s.ds_dly",
        "hr_3s",
        timezone
      );

      const categorizedData: AwaitableReturn<getPatientDataFromCDL>["data"] = {
        "1D": [],
        "3D": [],
        "5D": [],
        "30D": [],
        dailyExtra: [],
        hourlyExtra: [],
      };

      const thirtyDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(31, "days");
      const thirtySevenDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(37, "days");
      processedDailyData.forEach((item) => {
        // Convert the item's timestamp into a date/time object using the appropriate library method
        const date = timeProvider.dateLibrary(item.dt);

        // Check if the item's date is after the threshold
        if (date.isAfter(thirtyDaysAgo)) {
          categorizedData["30D"].push(item);
        }
        if (date.isAfter(thirtySevenDaysAgo)) {
          categorizedData["dailyExtra"].push(item);
        }
      });
      processedHourlyData.forEach((item) => {
        const date = timeProvider.dateLibrary.tz(item.dt, timezone);
        const now = timeProvider.dateLibrary(params.date_to);

        // Last 24 hours
        if (date.isAfter(now.clone().subtract(25, "hours"))) {
          categorizedData["1D"].push(item);
        }
        // Last 72 hours
        if (date.isAfter(now.clone().subtract(73, "hours"))) {
          categorizedData["3D"].push(item);
        }
        // Last 120 hours (5 days)
        if (date.isAfter(now.clone().subtract(121, "hours"))) {
          categorizedData["5D"].push(item);
        }
        if (date.isAfter(now.clone().subtract(145, "hours"))) {
          categorizedData["hourlyExtra"].push(item);
        }
      });
      return {
        success: true,
        data: categorizedData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  return {
    getPatientDataFromCDL,
    getBedExitMotionData,
    getHeartRateData,
    getPatientDailyRRData,
    getPatientDailyRRWithoutProcessing,
    getPatientHourlyRRWithoutProcessingV2,
    getPatientHourlyHRWithoutProcessingV2,
  };
};
